import { useState, type FormEvent } from 'react';
import type { DeviceIdentity } from '@pos/local-db';
import { env } from '../../lib/env.ts';
import { activeDbName, db, forgetEmptyBusinessDb, knownBusinesses, openBusinessDb } from '../../lib/db.ts';
import { useOnline } from '../../lib/use-online.ts';
import { enrollThisDevice, resetThisDevice, suggestDeviceName } from '../../services/device.ts';
import { completeOwnerSetup, ownerSignInForSetup, type OwnedBusiness } from '../../services/session.ts';
import { ForgotPassword } from '../auth/ForgotPassword.tsx';
import { Alert, Button, Card, Field, Input, errorMessage, PasswordInput } from '../../ui/index.tsx';

type Step = { name: 'login' } | { name: 'choose'; owned: OwnedBusiness[] } | { name: 'device'; chosen: OwnedBusiness };

/**
 * Primer uso del dispositivo: el dueño inicia sesión, elige el negocio y le pone un nombre
 * al dispositivo. Si quedó a medias (pendingDevice), se retoma bajando los datos.
 */
export function SetupPage({ pendingDevice }: { pendingDevice?: DeviceIdentity }) {
  const online = useOnline();
  const [step, setStep] = useState<Step>({ name: 'login' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [deviceName, setDeviceName] = useState(suggestDeviceName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const finish = async (chosen: OwnedBusiness) => {
    await completeOwnerSetup(chosen.membership, email, password);
    if (!(await db.businesses.get(chosen.membership.business_id))) {
      throw new Error('El dispositivo quedó habilitado pero no se pudieron descargar los datos. Revisá la conexión y probá de nuevo.');
    }
  };

  const onLogin = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const owned = await ownerSignInForSetup(email, password);
      if (pendingDevice) {
        const match = owned.find((o) => o.membership.business_id === pendingDevice.business_id);
        if (!match) throw new Error('Esta cuenta no es dueña del negocio al que pertenece este dispositivo.');
        await finish(match);
        return;
      }
      setStep(owned.length === 1 ? { name: 'device', chosen: owned[0]! } : { name: 'choose', owned });
    });
  };

  const onEnroll = (e: FormEvent) => {
    e.preventDefault();
    if (step.name !== 'device') return;
    void run(async () => {
      await enrollThisDevice(step.chosen.membership.business_id, deviceName);
      await finish(step.chosen);
    });
  };

  return (
    <div className="app-canvas flex min-h-full items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <img src="/favicon.svg" alt="" className="mx-auto size-14" />
          <h1 className="mt-3 text-2xl font-semibold">{env.appName}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {pendingDevice ? 'Terminemos de configurar este dispositivo' : 'Configurar este dispositivo'}
          </p>
        </div>

        <Card className="space-y-4">
          {!online && (
            <Alert tone="warning" title="Sin conexión">
              Para configurar un dispositivo nuevo hace falta internet. Después funciona sin conexión.
            </Alert>
          )}
          {error && <Alert tone="error">{error}</Alert>}

          {step.name === 'login' && (
            <>
            <form className="space-y-4" onSubmit={onLogin}>
              <p className="text-sm text-slate-600">
                Ingresá con la cuenta del <strong>dueño</strong> del negocio. Los empleados entran después, con su usuario y PIN.
              </p>
              <Field label="Email">
                {(id) => (
                  <Input id={id} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                )}
              </Field>
              <Field label="Contraseña">
                {(id) => (
                  <PasswordInput
                    id={id}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                )}
              </Field>
              <Button type="submit" size="lg" block loading={busy} disabled={!online}>
                Continuar
              </Button>
            </form>
            <ForgotPassword email={email} />
            </>
          )}

          {step.name === 'choose' && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">¿Para qué negocio es este dispositivo?</p>
              {step.owned.map((o) => (
                <Button
                  key={o.membership.business_id}
                  variant="secondary"
                  size="lg"
                  block
                  onClick={() => setStep({ name: 'device', chosen: o })}
                >
                  {o.businessName}
                </Button>
              ))}
            </div>
          )}

          {step.name === 'device' && (
            <form className="space-y-4" onSubmit={onEnroll}>
              <p className="text-sm text-slate-600">
                Vas a habilitar este dispositivo para <strong>{step.chosen.businessName}</strong>.
              </p>
              <Field label="Nombre del dispositivo" hint="Por ejemplo: Mostrador, Caja 2, Celular de Ana.">
                {(id) => (
                  <Input id={id} required maxLength={40} value={deviceName} onChange={(e) => setDeviceName(e.target.value)} />
                )}
              </Field>
              <Button type="submit" size="lg" block loading={busy} disabled={!online}>
                Habilitar dispositivo
              </Button>
            </form>
          )}
        </Card>

        <BackToOtherBusiness configuring={!!pendingDevice} />
        {pendingDevice && (
          <p className="text-center text-sm text-slate-500">
            ¿Te equivocaste de negocio?{' '}
            <button type="button" className="font-medium text-brand underline" onClick={() => void run(resetThisDevice)}>
              Empezar de nuevo
            </button>
          </p>
        )}
      </div>
    </div>
  );
}

/** Si en este dispositivo ya hay otros negocios, se puede volver a uno (por ejemplo, si se tocó «Agregar» por error). */
function BackToOtherBusiness({ configuring }: { configuring: boolean }) {
  const others = knownBusinesses().filter((b) => b.db !== activeDbName() && b.id);
  if (others.length === 0) return null;
  return (
    <p className="text-center text-sm text-slate-500">
      Volver a{' '}
      {others.map((b, i) => (
        <span key={b.db}>
          {i > 0 && ' · '}
          <button
            type="button"
            className="font-medium text-brand underline"
            onClick={() => {
              if (!configuring) forgetEmptyBusinessDb(activeDbName());
              openBusinessDb(b.db);
            }}
          >
            {b.name}
          </button>
        </span>
      ))}
    </p>
  );
}
